import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  uploadClinicFile,
  createClinicFile,
  getClinicFiles,
  updateClinicFile,
  deleteClinicFile,
  createClinicFolder,
  getClinicFolders,
  updateClinicFolder,
  deleteClinicFolder,
  getClinicStorageUsage,
} from "../../firebase";

export const clinicFilesApi = createApi({
  reducerPath: "clinicFilesApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["ClinicFile", "ClinicFolder", "StorageUsage"],
  endpoints: (builder) => ({
    // ============================================
    // FILES
    // ============================================

    // Get clinic files
    getClinicFiles: builder.query({
      async queryFn({ clinicId, folderId }) {
        try {
          const files = await getClinicFiles(clinicId, { folderId });
          return { data: files };
        } catch (error) {
          console.error("[CK-7920] Error fetching clinic files:", {
            errorCode: "CK-7920",
            errorMessage: error.message,
            clinicId,
            folderId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "ClinicFile", id })),
              { type: "ClinicFile", id: "LIST" },
            ]
          : [{ type: "ClinicFile", id: "LIST" }],
    }),

    // Upload and create clinic file
    uploadClinicFile: builder.mutation({
      async queryFn({
        file,
        clinicId,
        therapistId,
        therapistName,
        folderId,
        description,
      }) {
        try {
          const uploadResult = await uploadClinicFile(
            file,
            clinicId,
            therapistId
          );

          const fileData = {
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            fileUrl: uploadResult.url,
            storagePath: uploadResult.storagePath,
            folderId: folderId || null,
            description: description || null,
          };

          const fileRecord = await createClinicFile(
            clinicId,
            fileData,
            therapistId,
            therapistName
          );

          return { data: fileRecord };
        } catch (error) {
          console.error("[CK-7921] Error uploading clinic file:", {
            errorCode: "CK-7921",
            errorMessage: error.message,
            clinicId,
            fileName: file?.name,
            fileSize: file?.size,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [
        { type: "ClinicFile", id: "LIST" },
        { type: "StorageUsage", id: "USAGE" },
      ],
    }),

    // Update clinic file (description, folder)
    updateClinicFile: builder.mutation({
      async queryFn({ clinicId, fileId, fileData }) {
        try {
          const file = await updateClinicFile(clinicId, fileId, fileData);
          return { data: file };
        } catch (error) {
          console.error("[CK-7922] Error updating clinic file:", {
            errorCode: "CK-7922",
            errorMessage: error.message,
            clinicId,
            fileId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { fileId }) => [
        { type: "ClinicFile", id: fileId },
        { type: "ClinicFile", id: "LIST" },
      ],
    }),

    // Delete clinic file
    deleteClinicFile: builder.mutation({
      async queryFn({ clinicId, fileId, therapistId, therapistName }) {
        try {
          await deleteClinicFile(clinicId, fileId, therapistId, therapistName);
          return { data: fileId };
        } catch (error) {
          console.error("[CK-7923] Error deleting clinic file:", {
            errorCode: "CK-7923",
            errorMessage: error.message,
            clinicId,
            fileId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [
        { type: "ClinicFile", id: "LIST" },
        { type: "StorageUsage", id: "USAGE" },
      ],
    }),

    // ============================================
    // FOLDERS
    // ============================================

    // Get clinic folders
    getClinicFolders: builder.query({
      async queryFn({ clinicId }) {
        try {
          const folders = await getClinicFolders(clinicId);
          return { data: folders };
        } catch (error) {
          console.error("[CK-7924] Error fetching clinic folders:", {
            errorCode: "CK-7924",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "ClinicFolder", id })),
              { type: "ClinicFolder", id: "LIST" },
            ]
          : [{ type: "ClinicFolder", id: "LIST" }],
    }),

    // Create clinic folder
    createClinicFolder: builder.mutation({
      async queryFn({ clinicId, folderData, therapistId, therapistName }) {
        try {
          const folder = await createClinicFolder(
            clinicId,
            folderData,
            therapistId,
            therapistName
          );
          return { data: folder };
        } catch (error) {
          console.error("[CK-7925] Error creating clinic folder:", {
            errorCode: "CK-7925",
            errorMessage: error.message,
            clinicId,
            folderData,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "ClinicFolder", id: "LIST" }],
    }),

    // Update clinic folder
    updateClinicFolder: builder.mutation({
      async queryFn({ clinicId, folderId, folderData }) {
        try {
          const folder = await updateClinicFolder(
            clinicId,
            folderId,
            folderData
          );
          return { data: folder };
        } catch (error) {
          console.error("[CK-7926] Error updating clinic folder:", {
            errorCode: "CK-7926",
            errorMessage: error.message,
            clinicId,
            folderId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { folderId }) => [
        { type: "ClinicFolder", id: folderId },
        { type: "ClinicFolder", id: "LIST" },
      ],
    }),

    // Delete clinic folder
    deleteClinicFolder: builder.mutation({
      async queryFn({ clinicId, folderId }) {
        try {
          await deleteClinicFolder(clinicId, folderId);
          return { data: folderId };
        } catch (error) {
          console.error("[CK-7927] Error deleting clinic folder:", {
            errorCode: "CK-7927",
            errorMessage: error.message,
            clinicId,
            folderId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "ClinicFolder", id: "LIST" }],
    }),

    // ============================================
    // STORAGE (reuses same clinic-level storage)
    // ============================================

    getStorageUsage: builder.query({
      async queryFn({ clinicId }) {
        try {
          const usage = await getClinicStorageUsage(clinicId);
          return { data: usage };
        } catch (error) {
          console.error("[CK-7928] Error fetching storage usage:", {
            errorCode: "CK-7928",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "StorageUsage", id: "USAGE" }],
    }),
  }),
});

export const {
  // Files
  useGetClinicFilesQuery,
  useUploadClinicFileMutation,
  useUpdateClinicFileMutation,
  useDeleteClinicFileMutation,
  // Folders
  useGetClinicFoldersQuery,
  useCreateClinicFolderMutation,
  useUpdateClinicFolderMutation,
  useDeleteClinicFolderMutation,
  // Storage
  useGetStorageUsageQuery,
} = clinicFilesApi;
